// sections/SectionLocation.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";

import CitySingleSelectionModal from "modals/CitySingleSelectionModal";
import CityMultiSelectionModal from "modals/CityMultiSelectionModal";

import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import L from "leaflet";

const violetIcon = L.icon({
  iconUrl:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24">
        <path fill="#AE8FD2" stroke="white" stroke-width="1.5"
          d="M12 2c-3.314 0-6 2.686-6 6 0 4.5 6 14 6 14s6-9.5 6-14c0-3.314-2.686-6-6-6zm0 8.2a2.2 2.2 0 1 1 0-4.4 2.2 2.2 0 0 1 0 4.4z"/>
      </svg>
    `),
  iconSize: [34, 34],
  iconAnchor: [17, 32],
});

function ensureArray(val) {
  return Array.isArray(val) ? val : [];
}

function makeLabelMap(options) {
  const m = new Map();
  (options || []).forEach((o) => m.set(o.value, o.label));
  return m;
}

function parseCoord(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(",", ".").trim());
  return Number.isFinite(n) ? n : null;
}

function clampLatLng(lat, lng) {
  if (lat === null || lng === null) return [null, null];
  if (lat < -90 || lat > 90) return [null, null];
  if (lng < -180 || lng > 180) return [null, null];
  return [lat, lng];
}

function normalizeShowLocation(value) {
  return value === true || value === "true";
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              {title}
            </Typography>

            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {right}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {children}
      </CardContent>
    </Card>
  );
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
}

function MapRecenter({ lat, lng }) {
  const map = useMap();

  useEffect(() => {
    if (lat == null || lng == null) return;
    map.setView([lat, lng], map.getZoom(), { animate: true });
  }, [lat, lng, map]);

  return null;
}

function LocationMap({ lat, lng, hasValidCoords, onPick }) {
  const fallbackLat = 37.9838;
  const fallbackLng = 23.7275;

  const markerLat = hasValidCoords ? lat : fallbackLat;
  const markerLng = hasValidCoords ? lng : fallbackLng;

  return (
    <Box
      sx={{
        height: { xs: 260, md: 360, lg: 480 },
        width: "100%",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.18)",
      }}
    >
      <MapContainer
        center={[markerLat, markerLng]}
        zoom={10}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://carto.com/">CARTO</a> contributors'
        />

        <MapClickHandler onPick={onPick} />
        <MapRecenter lat={hasValidCoords ? lat : null} lng={hasValidCoords ? lng : null} />

        {hasValidCoords ? (
          <Marker position={[lat, lng]} icon={violetIcon} />
        ) : (
          <Circle
            center={[markerLat, markerLng]}
            radius={10000}
            pathOptions={{
              color: "#AE8FD2",
              fillColor: "#AE8FD2",
              fillOpacity: 0.3,
            }}
          />
        )}
      </MapContainer>
    </Box>
  );
}

export default function SectionLocation({
  form,
  setField,
  setLocation,
  cityOptions,
  updateLocationSection,
  saving,
  dirty,
  onSaved,
}) {
  const { t } = useTranslation();
  const [townOpen, setTownOpen] = useState(false);
  const [coversOpen, setCoversOpen] = useState(false);
  const [initialLocationSnapshot, setInitialLocationSnapshot] = useState(null);

  const labelMap = useMemo(() => makeLabelMap(cityOptions || []), [cityOptions]);

  const showLocation = normalizeShowLocation(form.location?.showLocation);
  const coversEverywhere = Boolean(form.coversEverywhere);
  const isPlace = form.parentCategory === "Places";

  const currentLocationSnapshot = useMemo(
    () =>
      JSON.stringify({
        town: form.location?.town || "",
        coords:
          ensureArray(form.location?.coords).length === 2
            ? ensureArray(form.location.coords).map(String)
            : ["", ""],
        showLocation,
        address: form.location?.address || "",
        postal_code: form.location?.postal_code || "",
        covers: ensureArray(form.covers),
        coversEverywhere,
      }),
    [
      form.location?.town,
      form.location?.coords,
      form.location?.showLocation,
      form.location?.address,
      form.location?.postal_code,
      form.covers,
      form.coversEverywhere,
      showLocation,
      coversEverywhere,
    ]
  );

  useEffect(() => {
    if (initialLocationSnapshot === null) {
      setInitialLocationSnapshot(currentLocationSnapshot);
    }
  }, [currentLocationSnapshot, initialLocationSnapshot]);

  const locationDirty =
    initialLocationSnapshot !== null && currentLocationSnapshot !== initialLocationSnapshot;

  const latStr = form.location?.coords?.[0] || "";
  const lngStr = form.location?.coords?.[1] || "";

  const latNum = parseCoord(latStr);
  const lngNum = parseCoord(lngStr);
  const [clampedLat, clampedLng] = clampLatLng(latNum, lngNum);
  const hasValidCoords = clampedLat != null && clampedLng != null;

  const onTownPick = (townNameEnOrCustom) => {
    setLocation("town", townNameEnOrCustom);

    const match = (cityOptions || []).find((x) => x.value === townNameEnOrCustom);

    if (match?.coords?.length === 2) {
      setLocation("coords", [String(match.coords[0]), String(match.coords[1])]);
    }
  };

  const onMapPick = (lat, lng) => {
    setLocation("coords", [String(lat), String(lng)]);
  };

  const save = async () => {
    const payload = {
      location: {
        town: form.location?.town || "",
        coords:
          ensureArray(form.location?.coords).length === 2
            ? ensureArray(form.location.coords).map(String)
            : ["", ""],
        showLocation: Boolean(showLocation),
        realAddress: [form.location?.address, form.location?.postal_code].filter(Boolean).join(", "),
      },
      covers: ensureArray(form.covers),
      coversEverywhere,
    };

    const ok = await updateLocationSection(payload);

    if (ok) {
      setInitialLocationSnapshot(currentLocationSnapshot);
      onSaved?.();
    }
  };

  return (
    <SectionCard
      title={t("info.location.sectionTitle")}
      subtitle={t("info.location.subtitle")}
      right={
        <Button variant="contained" onClick={save} disabled={!locationDirty || saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            onClick={() => setTownOpen(true)}
            role="button"
            tabIndex={0}
            sx={{
              border: "1px solid rgba(255,255,255,0.35)",
              borderRadius: 2,
              p: 2,
              cursor: "pointer",
              bgcolor: "rgba(255,255,255,0.06)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={900} sx={{ color: "#fff" }}>
                {t("info.location.homeCity")}
              </Typography>
            </Stack>

            <Stack sx={{ mt: 1 }}>
              {!form.location?.town ? (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.80)" }}>
                  {t("info.location.clickToSelect")}
                </Typography>
              ) : (
                <Chip
                  label={labelMap.get(form.location.town) || form.location.town}
                  size="small"
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: "rgba(255,255,255,0.28)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.65)",
                    fontWeight: 700,
                    "& .MuiChip-label": { color: "#fff" },
                  }}
                />
              )}
            </Stack>
          </Box>
        </Grid>

        {!isPlace && (
          <Grid item xs={12} md={6}>
            <Box
              onClick={() => setCoversOpen(true)}
              role="button"
              tabIndex={0}
              sx={{
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 2,
                p: 2,
                cursor: "pointer",
                bgcolor: "rgba(255,255,255,0.06)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontWeight={900} sx={{ color: "#fff" }}>
                  {t("info.location.coversCities")}
                </Typography>

                <Box
                  sx={{
                    minWidth: 34,
                    height: 26,
                    px: 1,
                    borderRadius: 999,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(255,255,255,0.22)",
                    border: "1px solid rgba(255,255,255,0.45)",
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: 13,
                  }}
                >
                  {coversEverywhere ? "∞" : ensureArray(form.covers).length}
                </Box>
              </Stack>

              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: "6px 8px" }}>
                {coversEverywhere ? (
                  <Chip
                    label="Εξυπηρέτηση παντού"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.28)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.65)",
                      fontWeight: 700,
                      "& .MuiChip-label": { color: "#fff" },
                    }}
                  />
                ) : ensureArray(form.covers).length === 0 ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.80)" }}>
                    {t("info.location.clickToSelect")}
                  </Typography>
                ) : (
                  <>
                    {ensureArray(form.covers).slice(0, 8).map((v) => (
                      <Chip
                        key={v}
                        label={labelMap.get(v) || v}
                        size="small"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.28)",
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.65)",
                          fontWeight: 700,
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    ))}
                    {ensureArray(form.covers).length > 8 && (
                      <Chip
                        size="small"
                        label={`+${ensureArray(form.covers).length - 8}`}
                        sx={{
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.14)",
                          border: "1px dashed rgba(255,255,255,0.55)",
                          fontWeight: 800,
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <TextField
            label={t("info.location.address")}
            value={form.location?.address || ""}
            onChange={(e) => setLocation("address", e.target.value)}
            fullWidth
            placeholder="π.χ. Ερμού 12"
            InputLabelProps={{ sx: { color: "#fff" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
              "& input::placeholder": { color: "rgba(255,255,255,0.45)" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label={t("info.location.postalCode")}
            value={form.location?.postal_code || ""}
            onChange={(e) => setLocation("postal_code", e.target.value)}
            fullWidth
            placeholder="π.χ. 10552"
            InputLabelProps={{ sx: { color: "#fff" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
              "& input::placeholder": { color: "rgba(255,255,255,0.45)" },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={showLocation}
                onChange={(e) => setLocation("showLocation", e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography fontWeight={900} sx={{ color: "#fff" }}>
                  {t("info.location.showOnProfile")}
                </Typography>

                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                  {t("info.location.showOnProfileHint")}
                </Typography>
              </Box>
            }
            sx={{
              m: 0,
              gap: 2,
              alignItems: "center",
              color: "#fff",
              p: 2,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.35)",
              width: "100%",
            }}
          />
        </Grid>

        {showLocation ? (
          <>
            <Grid item xs={12} md={3}>
              <Stack spacing={2}>
                <TextField
                  label="Latitude"
                  value={latStr}
                  onChange={(e) =>
                    setLocation("coords", [e.target.value, form.location?.coords?.[1] || ""])
                  }
                  fullWidth
                  helperText="e.g. 37.9838"
                  FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.75)" } }}
                  InputLabelProps={{ sx: { color: "#fff" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                      "&.Mui-focused fieldset": { borderColor: "#fff" },
                    },
                  }}
                />

                <TextField
                  label="Longitude"
                  value={lngStr}
                  onChange={(e) =>
                    setLocation("coords", [form.location?.coords?.[0] || "", e.target.value])
                  }
                  fullWidth
                  helperText="e.g. 23.7275"
                  FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.75)" } }}
                  InputLabelProps={{ sx: { color: "#fff" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                      "&.Mui-focused fieldset": { borderColor: "#fff" },
                    },
                  }}
                />

                <Alert
                  severity="info"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    "& .MuiAlert-icon": { color: "#fff" },
                  }}
                >
                  {t("info.location.coordsHint")}
                </Alert>

                {!hasValidCoords && (latStr || lngStr) ? (
                  <Alert
                    severity="warning"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.25)",
                      "& .MuiAlert-icon": { color: "#fff" },
                    }}
                  >
                    {t("info.location.invalidCoords")}
                  </Alert>
                ) : null}
              </Stack>
            </Grid>

            <Grid item xs={12} md={9}>
              <LocationMap
                lat={clampedLat}
                lng={clampedLng}
                hasValidCoords={hasValidCoords}
                onPick={onMapPick}
              />
            </Grid>
          </>
        ) : null}
      </Grid>

      <CitySingleSelectionModal
        open={townOpen}
        onClose={() => setTownOpen(false)}
        options={cityOptions || []}
        value={form.location?.town || ""}
        onSelect={onTownPick}
      />

      {!isPlace && (
        <CityMultiSelectionModal
          open={coversOpen}
          onClose={() => setCoversOpen(false)}
          options={cityOptions || []}
          value={ensureArray(form.covers)}
          onChange={(arr) => setField("covers", arr)}
          coversEverywhere={coversEverywhere}
          onCoversEverywhereChange={(val) => setField("coversEverywhere", val)}
        />
      )}
    </SectionCard>
  );
}